import { addEvent, removeEvent } from '../utils/events';
import { addClasses, removeElement, setStyles } from '../utils/html';

const defaultToastsOptions = {
  container: 'body',
  cssClass: 'js-toasts',
  maxToasts: 3,
  position: 'top|right',
};

const defaultToastOptions = {
  message: '',
  type: 'info',
  minDuration: 2000,
  maxDuration: 7000,
  outline: false,
};

const defaultIcons = {
  'info': 'fa fa-info',
  'success': 'fa fa-check',
  'warning': 'fa fa-exclamation',
  'error': 'fa fa-remove',
};

const DURATION_PER_CHARACTER = 50;
const DELAY = 100;
class Toast {

  constructor(container, options = {}) {
    this.toastsEl = container;

    options = { ...defaultToastOptions, ...options };

    this.render(options);
    setTimeout(() => this.show(), 0);
  }

  render(options = {}) {
    const { icon, message, type, outline } = options;
    const toastIconEl		  = document.createElement('span');
    const toastMessageEl	= document.createElement('span');
    this.toastDismissBtn	= document.createElement('button');
    // Create the toast element
    this.toastEl = document.createElement('li');

    // Apply the relevant css classes to the toast element
    const toastClasses = [ 'toast', `toast--${ type }` ];
    if (outline) {
      toastClasses.push('toast--outline');
    }
    addClasses(this.toastEl, toastClasses);

    // Determine the duration of the toast
    this.duration = this.getDuration(message, options);

    // Set up the toast icon element
    toastIconEl.innerHTML = `<i class="${ icon || defaultIcons[type] }"></i>`;
    toastIconEl.classList.add('toast__icon');

    // Set up the toast message element
    toastMessageEl.innerHTML = message;
    toastMessageEl.classList.add('toast__message');

    // Set up the dismiss button
    this.toastDismissBtn.innerHTML = '<i class="fa fa-remove"></i>';
    this.toastDismissBtn.classList.add('toast__action');

    // Bind the 'click' event handler for the dismiss button
    addEvent(this.toastDismissBtn, 'click', this.dismiss.bind(this));

    // Apply the 'is-landing' css class to the toast to provide the animations
    this.toastEl.classList.add('is-landing');

    // Finally, inject the elements to the DOM
    this.toastEl.appendChild(toastIconEl);
    this.toastEl.appendChild(toastMessageEl);
    this.toastEl.appendChild(this.toastDismissBtn);
    this.toastsEl.appendChild(this.toastEl);
  }

  /**
   * Calculates the duration that the message should be displayed for.
   * Based on 50ms per character, with a default minimum duration of 2s and a default maximum of 7s.
   * @param {String} message The message to be displayed in the toast.
   * @param {Object} minDuration, maxDuration The minimum and maximum duration that the toast should be displayed for.
   */
  getDuration(message = '', { minDuration, maxDuration }) {
    let duration = message.replace(' ', '').length * DURATION_PER_CHARACTER;
    if (duration < minDuration) {
      duration = minDuration;
    } else if (duration > maxDuration) {
      duration = maxDuration;
    }
    return duration;
  }

  show() {
    setTimeout(() => this.toastEl.classList.remove('is-landing'), DELAY);
    this.startTimer();
    removeEvent(this.toastEl, 'mouseleave', this.show);
  }

  startTimer() {
    this.timer = setTimeout(() => this.dismiss(), this.duration);
  }

  stopTimer() {
    clearTimeout(this.timer);
  }

  dismiss() {
    if (this.isDismissing) {
      return;
    }

    this.isDismissing = true;

    this.onDismissTransitionEnd = () => {
      removeElement(this.toastEl);
      removeEvent(this.toastDismissBtn, 'click', this.dismiss);
      removeEvent(this.toastEl, 'mouseeneter', this.cancelDismiss);
      removeEvent(this.toastEl, 'transitionend', this.onDismissTransitionEnd);
    };

    addEvent(this.toastEl, 'mouseenter', this.cancelDismiss.bind(this));
    addEvent(this.toastEl, 'transitionend', this.onDismissTransitionEnd);

    this.toastEl.classList.add('is-dismissing');
  }

  cancelDismiss() {
    this.isDismissing = false;
    this.stopTimer();
    removeEvent(this.toastEl, 'mouseenter', this.cancelDismiss);
    removeEvent(this.toastEl, 'transitionend', this.onDismissTransitionEnd);
    this.toastEl.classList.remove('is-dismissing');
    addEvent(this.toastEl, 'mouseleave', this.show.bind(this));
  }

}

class Toasts {

  constructor(options = {}) {
    this.init(options);
    return this;
  }

  init(options) {
    this.toasts = [];
    this.toastsEl = this.getContainer(options);
    this.setPositioning(options);

    // Set the maximum number of toasts allowed
    this.maxToasts = options.maxToasts;
  }

  getContainer(options = {}) {
    const { container, cssClass, position } = options;
    let toastsEl = document.querySelector(`.${ cssClass }`);
    if (!toastsEl) {
      toastsEl = document.createElement('ul');
      const toastClasses = [ 'toasts', `${ cssClass }` ];
      const positions = position.split('|');
      for (const pos of positions) {
        toastClasses.push(`toasts--position-${ pos }`);
      }
      addClasses(toastsEl, toastClasses);
      const toastsContainer = document.querySelector(container);
      toastsContainer.insertBefore(toastsEl, toastsContainer.firstChild);
    }

    return toastsEl;
  }

  /**
   * Checks the options for positioning instructions and applies them accordingly.
   * @param {*} options The configuration options specified when initializing the Toasts.
   */
  setPositioning(options) {
    const { top, right, bottom, left } = options;
    if (top || right || bottom || left) {
      let styles = {};
      // Apply either top OR bottom, not both
      if (top) {
        styles = { ...styles, top: `${ top }px` };
      } else if (bottom) {
        styles = { ...styles, bottom: `${ bottom }px` };
      }
      // Apply either right OR left, not both
      if (right) {
        styles = { ...styles, right: `${ right }px` };
      } else if (left)	{
        styles = { ...styles, left: `${ left }px` };
      }
      setStyles(this.toastsEl, styles);
    }
  }

  add(options = {}) {
    const toast = new Toast(this.toastsEl, options);
    this.clearOldToasts(options);
    this.toasts.push(toast);
  }

  clearOldToasts() {
    // Ensure the number of toasts does not exceed the maximum number of allowed toasts
    if (this.toasts.length < this.maxToasts) {
      return;
    }

    // If the number of toasts exceeds the maximum,
    // remove the first/oldest one and check again
    while (this.toasts.length >= this.maxToasts) {
      const oldToast = this.toasts.shift();
      oldToast.dismiss();
    }
  }

}

export default function initToasts(options = {}) {
  options = { ...defaultToastsOptions, ...options };
  return new Toasts(options);
}



// WEBPACK FOOTER //
// ./js/components/Toasts.js